// @flow

import type { Dispatch } from 'redux'
import { getPurchasedGiftCardCodeAsync, setGiftPurchaseAsync, setRedeemedOnUser } from '../models/Gift'
import type {
  PurchaseGiftFailType,
  PurchaseGiftSuccessType, PurchasingGiftType,
  RedeemGiftFailType,
  RedeemGiftSuccessType,
  RedeemingGiftType
} from './actionTypes'
import {
  REDEEM_GIFT_FAIL,
  REDEEMING_GIFT,
  REDEEM_GIFT_SUCCESS,
  GIFT_PURCHASE_FAIL,
  GIFT_PURCHASE_SUCCESS, GIFT_PURCHASE_PROCESSING
} from './actionTypes'
import { reportError } from '../services/bugSnag'
import { currentUser } from '../services/firebaseAuth'
import { events } from '../services/analytics'
import type { PurchaseGiftParamsType } from '../models/Gift'
import { onRecordChange, turnOffListeners } from '../services/firebaseDatabase'
import { openGiftConfirmPage, openRedeemConfirmPage } from './navigationActions'
import type { AppStateType } from '../reducers/appstate'
import { clearPromoCode } from './subscriptionActions'

export function redeemGift (): RedeemingGiftType {
  return {
    type: REDEEMING_GIFT
  }
}

export function redeemFail (msg: string): RedeemGiftFailType {
  return {
    type: REDEEM_GIFT_FAIL,
    msg
  }
}

export function redeemSucccess (): RedeemGiftSuccessType {
  return {
    type: REDEEM_GIFT_SUCCESS
  }
}

export function redeemCode (code: string | null): any {
  return async (dispatch: Dispatch<any>): Promise<any> => {
    try {
      dispatch(redeemGift())
      const user = currentUser()
      if (!user || !user.id) {
        return dispatch(redeemFail('Please sign in or create an account first'))
      }
      const codeExists = await getPurchasedGiftCardCodeAsync(code)
      if (codeExists && code) {
        await setRedeemedOnUser(user.id, code)
        events.redeemCode({
          gift_code: code,
          redeemed_at: Math.round((new Date()).getTime() / 1000) // unix time for Intercom w/ _at
        })
        dispatch(redeemSucccess())
        dispatch(openRedeemConfirmPage())
      } else {
        dispatch(redeemFail('Uh oh! Seems like that isn\'t a valid code... If you\'re having trouble, let us know at feedback@dipseastories.com'))
      }
    } catch (e) {
      dispatch(redeemFail('Uh oh! we had some sort of problem redeeming this gift.'))
      reportError(e)
    }
  }
}

export function purchaseGiftFail (message: string): PurchaseGiftFailType {
  return {
    type: GIFT_PURCHASE_FAIL,
    message
  }
}

export function purchaseGiftSuccess (giftCode: string): PurchaseGiftSuccessType {
  return {
    type: GIFT_PURCHASE_SUCCESS,
    giftCode
  }
}

export function purchaseGiftProcessing (): PurchasingGiftType {
  return {
    type: GIFT_PURCHASE_PROCESSING
  }
}

export function purchaseGiftAndGetCode (params: PurchaseGiftParamsType, name: string, stripe: any, cardElement: any): any {
  return async (dispatch: Dispatch<any>, getState: () => AppStateType): Promise<any> => {
    try {
      const state = getState()
      dispatch(purchaseGiftProcessing())
      const tokenObj = await stripe.createToken(cardElement, { type: 'card', name, email: params.purchaserEmail })
      if (!tokenObj) {
        return dispatch(purchaseGiftFail('credit card is not valid'))
      }
      params.token = tokenObj.token

      if (state.subscriptionPromo && state.subscriptionPromo.valid && state.subscriptionText?.price) {
        params.promo = state.subscriptionPromo.userEnteredPromo
        params.discount = state.subscriptionPromo.promo
        // $FlowFixMe
        params.price = parseInt((state.subscriptionText || {}).price.replace('$', '').replace('.', ''))
      }

      const purchaseKey = await setGiftPurchaseAsync(params)

      window.purchaseErrorListener = onRecordChange(`/giftCardPurchases/${purchaseKey}/error`, (error: string): void => {
        dispatch(purchaseGiftFail(error))
        turnOffListeners(['purchaseGiftCodeListener', 'purchaseErrorListener'])
      })
      window.purchaseGiftCodeListener = onRecordChange(`/giftCardPurchases/${purchaseKey}/giftCode`, (giftCode: string): void => {
        dispatch(purchaseGiftSuccess(giftCode))
        turnOffListeners(['purchaseGiftCodeListener', 'purchaseErrorListener'])
        dispatch(openGiftConfirmPage())
        dispatch(clearPromoCode())

        const eventParams = {}
        eventParams.giftCode = giftCode
        if (params.promo) eventParams.promoCode = params.promo
        if (params.subscriptionGiftType) eventParams.giftType = params.subscriptionGiftType
        if (params.price) eventParams.price = params.price
        if (params.discount) eventParams.discount = params.discount
        events.purchasedGiftCode(eventParams)
      })
    } catch (e) {
      reportError(e)
      return dispatch(purchaseGiftFail('We had an error processing this gift purchase'))
    }
  }
}
